<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BCard,
  BCardText,
  BCardBody,
  BCardTitle,
  BCardSubTitle,
  BCardFooter,
  BCardHeader,
  BCol,
  BRow,
} from 'bootstrap-vue';
import { ValidationObserver } from 'vee-validate';
import { FormInput, FormTextArea, FormSelect } from '@/views/apps/fbr-invoice/components/form';
import { ref, watch } from '@vue/composition-api';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'BusinessForm',
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BCard,
    BCardBody,
    BCardTitle,
    BCardSubTitle,
    BCardText,
    BCardFooter,
    BCardHeader,
    BCol,
    BRow,
    ToastificationContent,

    // Form Validation
    ValidationObserver,

    // Custom Components
    FormInput,
    FormTextArea,
    FormSelect,
  },
  props: {
    formData: {
      type: Object | null,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const businessFormRef = ref(null);
    const businessFormData = ref(
      props.formData
        ? JSON.parse(JSON.stringify(props.formData))
        : {
            id: 0,
            name: '',
            logo: '',
            ntn: '',
            cnic: '',
            province: '',
            address: '',
            phone: '',
            email: '',
            website: '',
          }
    );

    const provinceOptions = [
      { value: 'Punjab', text: 'Punjab' },
      { value: 'Sindh', text: 'Sindh' },
      { value: 'Khyber Pakhtunkhwa', text: 'Khyber Pakhtunkhwa' },
      { value: 'Balochistan', text: 'Balochistan' },
      { value: 'Gilgit-Baltistan', text: 'Gilgit-Baltistan' },
    ];

    const onSubmit = async () => {
      console.log('Emitting:', JSON.stringify(businessFormData.value, null, 2));
      emit('submit');
    };

    watch(
      businessFormData,
      (newVal) => {
        emit('update:form-data', newVal);
      },
      { deep: true }
    );

    const onClickSubmit = () => {
      businessFormRef.value.dispatchEvent(new Event('submit', { cancelable: true }));
    };

    const onClickCancel = () => {
      if (
        confirm('Are you sure you want to cancel?', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
      ) {
        emit('cancel');
      }
    };

    return {
      businessFormRef,
      businessFormData,
      provinceOptions,

      onSubmit,
      onClickCancel,
      onClickSubmit,
    };
  },
};
</script>

<template>
  <div>
    <div class="resource-header border-bottom d-flex justify-content-end align-items-start mb-1">
      <div class="actions-container d-flex justify-content-between">
        <b-button
          type="button"
          variant="outline-default"
          class="mr-1"
          :disabled="loading"
          @click="onClickCancel"
        >
          Cancel
        </b-button>
        <b-button type="submit" variant="primary" :disabled="loading" @click="onClickSubmit">
          Submit
        </b-button>
      </div>
    </div>
    <validation-observer>
      <b-form
        slot-scope="{ validate }"
        @submit.prevent="validate().then(onSubmit)"
        class="update-form"
        ref="businessFormRef"
      >
        <b-card no-body>
          <b-card-header>
            <b-card-title>Business Details</b-card-title>
          </b-card-header>

          <b-card-body>
            <b-row>
              <!-- Logo -->
              <b-col md="6">
                <image-field
                id="logo"
                  v-model="businessFormData.logo"
                  label="Logo"
                  name="Logo"
                  rules="required"
                />
              </b-col>
              <!-- Name -->
              <b-col md="6">
                <form-input
                  v-model="businessFormData.name"
                  label="Name"
                  name="Name"
                  rules="required"
                />
              </b-col>
              <!-- Phone -->
              <b-col md="6">
                <form-input
                  v-model="businessFormData.phone"
                  label="Phone"
                  name="Phone"
                  rules="required"
                />
              </b-col>
              <!-- Email -->
              <b-col md="6">
                <form-input
                  v-model="businessFormData.email"
                  label="Email"
                  name="Email"
                  rules="required"
                />
              </b-col>
              <!-- Website -->
              <b-col md="6">
                <form-input
                  v-model="businessFormData.website"
                  label="Website"
                  name="Website"
                  rules="required"
                />
              </b-col>
              <!-- Address -->
              <b-col md="12">
                <form-text-area
                  v-model="businessFormData.address"
                  label="Address"
                  name="Address"
                  rules="required"
                />
              </b-col>
              <!-- Province -->
              <b-col md="6">
                <form-select
                  v-model="businessFormData.province"
                  :disabled="loading"
                  label="Province"
                  name="Province"
                  rules="required"
                  :options="provinceOptions"
                />
              </b-col>
              <!-- NTN -->
              <b-col md="6">
                <form-input
                  v-model="businessFormData.ntn"
                  label="NTN Number"
                  name="NTN Number"
                  placeholder="Enter NTN number (7 digits)"
                  v-mask="'#######-#'"
                  rules="ntn"
                />
              </b-col>
              <!-- CNIC -->
              <b-col md="6">
                <form-input
                  v-model="businessFormData.cnic"
                  label="CNIC"
                  name="CNIC"
                  placeholder="Enter CNIC number (13 digits)"
                  v-mask="'#####-#######-#'"
                  rules="cnic"
                />
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-form>
    </validation-observer>
  </div>
</template>

<style lang="scss" scoped>
.resource-header {
  position: sticky;
  left: 0;
  top: 60px;
  padding: 1.3rem 0 1.3rem 0;
  background-color: var(--light);
  z-index: 999;
}

@media (max-width: 576px) {
  .resource-header {
    flex-direction: column-reverse !important;
    padding: 1.3rem 0 0 0;

    & > div:not(:first-child) {
      width: 100%;
      margin-bottom: 1rem;
    }
  }
}

.b-card {
  margin-bottom: 1rem;
}
</style>
